'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { marginsBunja } from '@flipster/r230522/original/margin';
import { marketsBunja } from '@flipster/r230522/original/market';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { getTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { calcEstLiquidationPriceWithFee } from '@flipster/utils/calculator/order-preview';
import { getQuoteCurrencyPrecision, type QuoteCurrencyList } from '@flipster/utils/constants/currency';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';
import type { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

import { NumberInputWithSuffix } from '../../../input/number-input-with-suffix';
import { LeverageBadge, OrderSideBadge } from '../../../misc/badge';
import { SymbolDisplay } from '../../../misc/display';
import { rawInstrumentsKeyBySymbolAtom } from '../../snapshot/state';
import type { SizeInputMode } from './popover';

const DropdownSelect = createDropdownSelect<SizeInputMode>();

interface AdjustOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit?: () => void;
  // Order info
  symbol: string;
  side: V2OrderSideType;
  initLeverage: number;
  marginType: OrderMarginType;
  isLimit: boolean;
  currency: string;
  quoteCurrency: (typeof QuoteCurrencyList)[number];
  pricePrecision: number;
  qtyPrecision: number;
  // Price info
  priceLabel: string;
  priceValue: string;
  // Size info
  qtyValue: string;
  // Action. Return a Promise to keep the modal open on rejection; resolve to close.
  onConfirm: (
    price: string | undefined,
    sizeValue: string | undefined,
    sizeMode: SizeInputMode,
  ) => void | Promise<void>;
}

export const AdjustOrderModal = ({
  isOpen,
  onClose,
  onExit,
  symbol,
  side,
  initLeverage,
  marginType,
  isLimit,
  currency,
  quoteCurrency,
  pricePrecision,
  qtyPrecision,
  priceLabel,
  priceValue,
  qtyValue,
  onConfirm,
}: AdjustOrderModalProps) => {
  const t = useTranslations();

  const [price, setPrice] = useState(priceValue);
  const [qty, setQty] = useState(qtyValue);
  const [sizeMode, setSizeMode] = useState<SizeInputMode>('quantity');
  const [priceDirty, setPriceDirty] = useState(false);
  const [qtyDirty, setQtyDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { marketsAtom } = useBunja(marketsBunja);
  const { pswapsAtom } = useBunja(pswapsBunja);
  const { getMarginAtom } = useBunja(marginsBunja);
  const markets = useAtomValue(marketsAtom);
  const pswaps = useAtomValue(pswapsAtom);
  const margins = useAtomValue(getMarginAtom);
  const instruments = useAtomValue(rawInstrumentsKeyBySymbolAtom);
  const referencePrice = getTickerDisplayPriceOrDefault(markets?.[symbol]);
  const instrument = instruments?.[symbol];
  const pswap = pswaps?.[symbol];
  const availableFunds =
    marginType === 'Cross'
      ? (margins[quoteCurrency]?.crossMarginAvailable ?? '0')
      : (margins[quoteCurrency]?.isolatedMarginAvailable ?? '0');

  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);
  const sizeUnit = sizeMode === 'quantity' ? currency : quoteCurrency;
  const sizePrecision = sizeMode === 'quantity' ? qtyPrecision : quoteCurrencyPrecision;

  const sizeLabel = {
    quantity: t('Position_add_position.r250401.Input_position_quantity_title'),
    usdt: t('Position_add_position.r250401.Input_position_usdt_title'),
    margin: t('Position_add_position.r250401.Input_margin_title'),
  }[sizeMode];

  // Est. total quantity derivation.
  // - When mode is quantity: input qty is in base currency → total value in quote = qty * price.
  // - When mode is usdt (position size) or margin: input is in quote currency → base qty ≈ qty / price.
  const quoteValue =
    sizeMode === 'quantity'
      ? Num(qty || '0')
          .multipliedBy(price || '0')
          .toString()
      : qty || '0';
  const baseValue = (() => {
    if (sizeMode === 'quantity') return qty || '0';
    const p = Num(price || '0');
    return p.isZero()
      ? '0'
      : Num(qty || '0')
          .dividedBy(p)
          .toString();
  })();

  const handleConfirm = async () => {
    const nextPrice = priceDirty ? price : undefined;
    const nextQty = qtyDirty ? qty : undefined;
    try {
      setIsSubmitting(true);
      await onConfirm(nextPrice, nextQty, sizeMode);
      onClose();
    } catch {
      // Keep the modal open on backend error; the mutation hook surfaces the toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset price/qty to the prefilled values in the current size mode each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setPrice(priceValue);
    const q = Num(qtyValue || '0');
    const p = Num(priceValue || '0');
    const nextQty =
      sizeMode === 'quantity'
        ? q.toFixed(qtyPrecision)
        : p.isZero()
          ? '0'
          : sizeMode === 'usdt'
            ? q.multipliedBy(p).toFixed(quoteCurrencyPrecision)
            : initLeverage > 0
              ? q.multipliedBy(p).dividedBy(initLeverage).toFixed(quoteCurrencyPrecision)
              : '0';
    setQty(nextQty);
    setPriceDirty(false);
    setQtyDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, priceValue, qtyValue]);

  const handleSizeModeChange = (nextMode: SizeInputMode) => {
    if (nextMode === sizeMode) return;
    const p = Num(price || '0');
    const entered = Num(qty || '0');
    if (!p.isZero() && !entered.isZero()) {
      const asQuantity =
        sizeMode === 'quantity'
          ? entered
          : sizeMode === 'usdt'
            ? entered.dividedBy(p)
            : initLeverage > 0
              ? entered.multipliedBy(initLeverage).dividedBy(p)
              : Num(0);
      const converted =
        nextMode === 'quantity'
          ? asQuantity
          : nextMode === 'usdt'
            ? asQuantity.multipliedBy(p)
            : initLeverage > 0
              ? asQuantity.multipliedBy(p).dividedBy(initLeverage)
              : Num(0);
      const nextPrecision = nextMode === 'quantity' ? qtyPrecision : quoteCurrencyPrecision;
      setQty(converted.toFixed(nextPrecision));
    }
    setSizeMode(nextMode);
  };

  const maxMarginAvailable = (() => {
    const fundsBased = BigNumber.max(Num(availableFunds), Num(0));
    // notionalMaxOrderAmount는 position size(=margin*leverage) 상한이므로 margin 단위로 환산해 함께 cap
    const notionalMax = pswap?.notionalMaxOrderAmount;
    if (!notionalMax || Num(notionalMax).isZero() || initLeverage <= 0) return fundsBased;
    const notionalMarginCap = Num(notionalMax).dividedBy(initLeverage);
    return BigNumber.min(fundsBased, notionalMarginCap);
  })();

  // Max size in the currently selected size mode, mirroring AddPositionModal/updateValueByPercent.
  // Slippage is applied only in quantity mode (multiply by 1 - defaultMaxSlippage).
  const maxSizeInMode = (() => {
    const p = Num(price || '0');
    if (sizeMode === 'margin') return maxMarginAvailable;
    if (initLeverage <= 0) return Num(0);
    const maxPositionSize = maxMarginAvailable.multipliedBy(initLeverage);
    if (sizeMode === 'usdt') return maxPositionSize;
    if (p.isZero()) return Num(0);
    const slippage = instrument?.defaultMaxSlippage ? Num(1).minus(instrument.defaultMaxSlippage) : Num('0.99');
    return maxPositionSize.dividedBy(p).multipliedBy(slippage);
  })();

  const handlePercent = (percent: number) => {
    if (maxSizeInMode.isZero()) return;
    setQty(maxSizeInMode.multipliedBy(percent).dividedBy(100).toFixed(sizePrecision));
    setQtyDirty(true);
  };

  // Max label is shown only in margin mode per spec; value is in quote currency.
  const maxLabel = t('Position_add_funds.r250828.Max_amount_quote_currency', {
    amount: numberFormatter(maxMarginAvailable, { precision: quoteCurrencyPrecision, toFixed: true }),
    quoteCurrency,
  });

  const estLiquidationPrice = (() => {
    if (marginType !== 'Isolated') return null;
    if (!instrument || !price || Num(price).isZero() || initLeverage <= 0) return null;
    try {
      return calcEstLiquidationPriceWithFee(
        side,
        initLeverage,
        price,
        instrument.maintMarginRate,
        pswap?.takerFeeRate ?? '0',
        pswap?.makerFeeRate ?? '0',
        instrument.tickSize,
      );
    } catch {
      return null;
    }
  })();

  const minQty = instrument ? Num(instrument.unitOrderQty) : Num(0);
  const minAmount = instrument ? Num(instrument.notionalMinOrderAmount) : Num(0);
  const minMargin = initLeverage > 0 ? minAmount.dividedBy(initLeverage) : Num(0);

  const sizeError = (() => {
    const entered = Num(qty || '0');
    if (entered.isZero()) return null;
    if (sizeMode === 'quantity' && !minQty.isZero() && entered.isLessThan(minQty)) {
      return t('Trade.r250401.Min_quantity_required_error', {
        symbol: currency,
        amount: numberFormatter(minQty, { precision: qtyPrecision, toFixed: true }),
      });
    }
    if (sizeMode === 'usdt' && !minAmount.isZero() && entered.isLessThan(minAmount)) {
      return t('Trade.r250401.Min_position_size_required_error', {
        amount: numberFormatter(minAmount, { precision: quoteCurrencyPrecision, toFixed: true }),
      });
    }
    if (sizeMode === 'margin' && !minMargin.isZero() && entered.isLessThan(minMargin)) {
      return t('Trade.r250401.Min_margin_required_error', {
        amount: numberFormatter(minMargin, { precision: quoteCurrencyPrecision, toFixed: true }),
      });
    }
    return null;
  })();

  const isUnchanged = !priceDirty && !qtyDirty;

  return (
    <Modal.Root isOpen={isOpen} onClose={isSubmitting ? undefined : onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label={t('Trade.r260414.Adjust_order')} />
      </Modal.Header>
      <Modal.Content>
        <div className={hstack({ gap: 16, alignItems: 'center', mb: 16 })}>
          <SymbolDisplay
            css={{ flex: 1, minW: 0 }}
            symbol={symbol}
            icon={<SymbolDisplay.Icon size={32} space={8} />}
            mainLabel={
              <SymbolDisplay.Label
                value='shortName'
                css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
              />
            }
            subLabel={
              <Formatted.Number
                css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-2' }}
                value={referencePrice}
                precision={pricePrecision}
                toFixed
              />
            }
          />
          <div className={hstack({ gap: 4, alignItems: 'center' })}>
            <OrderSideBadge orderSide={side} />
            <LeverageBadge leverage={initLeverage} marginType={marginType} />
          </div>
        </div>

        <div className={stack({ gap: 12 })}>
          <div className={stack({ bg: 'background.default.level-1', rounded: 8, px: 16, py: 12, gap: 6 })}>
            <div className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>{priceLabel}</div>
            <FormattedNumberInput
              className={css({
                textStyle: 'Title/M/Monospace',
                color: 'content.default.level-5',
              })}
              value={price}
              precision={pricePrecision}
              onChange={(v) => {
                setPrice(v || '0');
                setPriceDirty(true);
              }}
            />
          </div>

          <div className={stack({ bg: 'background.default.level-1', rounded: 8, px: 16, py: 12, gap: 8 })}>
            <div className={stack({ gap: 6 })}>
              <DropdownSelect.Root selected={sizeMode} onChange={handleSizeModeChange}>
                <DropdownSelect.Trigger
                  css={hstack.raw({ gap: 4, textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}
                >
                  {sizeLabel}
                  <ChevronDownIcon
                    size={18}
                    className={css({
                      transition: 'transform 0.2s',
                      '[aria-expanded=true] &': { transform: 'rotate(-180deg)' },
                    })}
                  />
                </DropdownSelect.Trigger>
                <DropdownSelect.Select portal css={{ w: 240 }}>
                  <DropdownSelect.Item
                    value='quantity'
                    label={t('Position_add_position.r250401.Input_position_quantity_title')}
                    subLabel={t('Position_add_position.r250401.Input_position_quantity_description')}
                  />
                  <DropdownSelect.Item
                    value='usdt'
                    label={t('Position_add_position.r250401.Input_position_usdt_title')}
                    subLabel={t('Position_add_position.r250401.Input_position_usdt_description')}
                  />
                  <DropdownSelect.Item
                    value='margin'
                    label={t('Position_add_position.r250401.Input_margin_title')}
                    subLabel={t('Position_add_position.r250401.Input_margin_description')}
                  />
                </DropdownSelect.Select>
              </DropdownSelect.Root>

              <div className={css({ h: 30 })}>
                <NumberInputWithSuffix
                  value={qty}
                  precision={sizePrecision}
                  onChange={(v) => {
                    setQty(v);
                    setQtyDirty(true);
                  }}
                  suffix={qty !== '' && <div className={css({ textStyle: 'Text/L/Strong' })}>{sizeUnit}</div>}
                  className={flex({
                    textStyle: 'Title/M/Monospace',
                    color: 'content.default.level-5',
                    align: 'flex-end',
                    gap: 4,
                  })}
                />
              </div>
              {sizeError && (
                <div
                  className={css({
                    textStyle: 'Text/S/Monospace/Regular',
                    color: 'content.state.negative',
                  })}
                >
                  {sizeError}
                </div>
              )}
            </div>

            <div
              className={hstack({
                py: 8,
                gap: 8,
                '& > button': {
                  flex: 1,
                  bg: 'background.default.level-3',
                  color: 'content.default.level-2',
                  textStyle: 'Text/S/Strong',
                  h: 41,
                  borderRadius: 4,
                  display: 'flex',
                  flexDir: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  px: 10,
                },
              })}
            >
              <button type='button' onClick={() => handlePercent(25)}>
                25%
              </button>
              <button type='button' onClick={() => handlePercent(50)}>
                50%
              </button>
              <button type='button' onClick={() => handlePercent(75)}>
                75%
              </button>
              <button type='button' onClick={() => handlePercent(100)}>
                <div>100%</div>
                {sizeMode === 'margin' && (
                  <div className={css({ whiteSpace: 'nowrap', textStyle: 'Text/XS/Regular' })}>{maxLabel}</div>
                )}
              </button>
            </div>
          </div>
        </div>

        <div
          className={css({
            borderTopWidth: '1px',
            borderTopStyle: 'solid',
            borderColor: 'border.default.level-2',
            mt: 16,
            pt: 16,
          })}
        >
          <dl
            className={stack({
              gap: 4,
              '& > div': {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 8,
              },
              '& dt': { textStyle: 'Text/L/Regular', color: 'content.default.level-2' },
              '& dd': { textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5', textAlign: 'right' },
            })}
          >
            <div>
              <dt>
                {sizeMode === 'quantity'
                  ? isLimit
                    ? t('Trade.r250401.Total_quantity')
                    : t('Trade.r260414.Est_total_quantity')
                  : isLimit
                    ? t('Trade.r250401.Total_position_size')
                    : t('Trade.r260414.Est_total_position_size')}
              </dt>
              <dd>
                {sizeMode === 'quantity' ? (
                  <>
                    <Formatted.Number value={baseValue} precision={qtyPrecision} toFixed suffix={currency} />
                    <div>
                      <Formatted.Number
                        value={quoteValue}
                        precision={quoteCurrencyPrecision}
                        toFixed
                        prefix='≈ '
                        suffix={quoteCurrency}
                        css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-1' }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Formatted.Number
                      value={quoteValue}
                      precision={quoteCurrencyPrecision}
                      toFixed
                      suffix={quoteCurrency}
                    />
                    <div>
                      <Formatted.Number
                        value={baseValue}
                        precision={qtyPrecision}
                        toFixed
                        prefix='≈ '
                        suffix={currency}
                        css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-1' }}
                      />
                    </div>
                  </>
                )}
              </dd>
            </div>
            {estLiquidationPrice !== null && (
              <div>
                <dt>{t('Trade.r260414.Est_liquidation_price')}</dt>
                <dd>
                  <Formatted.Number
                    value={estLiquidationPrice}
                    precision={pricePrecision}
                    toFixed
                    suffix={quoteCurrency}
                  />
                </dd>
              </div>
            )}
          </dl>
        </div>
      </Modal.Content>
      <Modal.Footer align='vertical'>
        <Modal.Button
          hierarchy='secondary'
          color='neutral'
          size='large'
          label={t('Common.r250401.Cancel')}
          onClick={onClose}
          disabled={isSubmitting}
        />
        <Modal.Button
          hierarchy='primary'
          color='accent'
          size='large'
          label={t('Common.r250401.Confirm')}
          onClick={handleConfirm}
          isLoading={isSubmitting}
          disabled={sizeError !== null || !qty || Num(qty).isZero() || !price || Num(price).isZero() || isUnchanged}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};
